import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Dropdown } from '@/components';
import { useSizes } from '@/utility/responsive.ts';
import Pagination from '@/components/pagination';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import '@/assets/styles/views/solver/expert-review.scss';
import { useFeatureFlag } from 'configcat-react';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { parseJwt, jsonTryParse } from '@/utility';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import {
	BranchLessonResponse,
	ExpertReviewReasonResponse,
	ExpertReviewWithQuestionDetailsResponse,
	ExpertResolveQuestionResponse,
	ExpertResolveQuestionResponseQuestionStatusEnum,
	PageExpertReviewWithQuestionDetailsResponse,
} from '@dopingtech/live_expert_bff_api_ts';

interface DropdownOption {
	id: string;
	name: string;
}

// listResolveQuestions thin (queue) rows eksik alanları (müfredat/ünite/puan/video) getReviewsByExpertId ile aynı şekilde göstermez;
// bu satırlar salt-okunur takip amaçlıdır, "Bana Ata" bu satırlarda anlamsız olduğu için gösterilmez.
type ReviewRow = Partial<ExpertReviewWithQuestionDetailsResponse> & {
	reassignedToExpert?: boolean;
	isResolveQueueRow?: boolean;
	resolveQuestionStatus?: ExpertResolveQuestionResponseQuestionStatusEnum;
};

const PENDING_RESOLVE_STATUSES: ExpertResolveQuestionResponseQuestionStatusEnum[] = [
	ExpertResolveQuestionResponseQuestionStatusEnum.SendingExpertPool,
	ExpertResolveQuestionResponseQuestionStatusEnum.InSolution,
];
const SOLVED_RESOLVE_STATUSES: ExpertResolveQuestionResponseQuestionStatusEnum[] = [ExpertResolveQuestionResponseQuestionStatusEnum.Solved];

const ReviewStatus = {
	PENDING: 'pending',
	REVIEWED: 'reviewed',
	REASSIGNED_PENDING: 'reassigned_pending',
	REASSIGNED_REVIEWED: 'reassigned_reviewed',
} as const;

const STATUS_OPTIONS: DropdownOption[] = [
	{ id: ReviewStatus.PENDING, name: 'Değerlendirilmeyi Bekleyenler' },
	{ id: ReviewStatus.REVIEWED, name: 'Değerlendirilenler' },
	{ id: ReviewStatus.REASSIGNED_PENDING, name: 'Yeniden Yanıt Bekleyenler' },
	{ id: ReviewStatus.REASSIGNED_REVIEWED, name: 'Yeniden Yanıtlananlar' },
];

const ITEMS_PER_PAGE = 10;

const ALL_BRANCHES_OPTION: DropdownOption = { id: 'all', name: 'Tüm Branşlar' };

const ExpertReviewScreen: React.FC = () => {
	const { isDesktop } = useSizes();
	const { ExpertReviewControllerApi, ExpertResolveQuestionControllerApi } = useApi();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	// REVIEWER_EXPERT_ID_LIST ile aynı desen: "Bana Ata" akışı uzman bazlı ConfigCat listesiyle açılır
	const { data: authData } = useAuthStore();
	const expertId = authData?.access_token ? parseJwt(authData.access_token)?.additional_info?.expert_id : null;
	const { value: assignToMeExpertIdListRaw } = useFeatureFlag(CONFIGCAT_KEYS.ASSIGN_TO_ME_EXPERT_ID_LIST, '[]');
	const isActiveAssignToMe = useMemo(
		() => !!expertId && jsonTryParse(assignToMeExpertIdListRaw as string).some((id: number) => id === parseInt(String(expertId))),
		[expertId, assignToMeExpertIdListRaw]
	);
	// flag kapalıyken "Bana Ata" akışı ve buna bağlı "Yeniden Atanan" filtreleri tamamen gizlenir
	const statusOptions = isActiveAssignToMe
		? STATUS_OPTIONS
		: STATUS_OPTIONS.filter(o => o.id !== ReviewStatus.REASSIGNED_PENDING && o.id !== ReviewStatus.REASSIGNED_REVIEWED);

	const [currentPage, setCurrentPage] = useState(1);
	const [selectedBranch, setSelectedBranch] = useState<DropdownOption>(ALL_BRANCHES_OPTION);
	const [selectedStatus, setSelectedStatus] = useState<DropdownOption | null>(STATUS_OPTIONS[0]);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [modalRating, setModalRating] = useState(0);
	const [hoveredStar, setHoveredStar] = useState(0);
	const [selectedReasons, setSelectedReasons] = useState<number[]>([]);

	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [lightboxImage, setLightboxImage] = useState('');

	const { data: branchOptions = [] } = useQueryProvider(
		[QUERY_KEYS.getExpertBranches],
		() => ExpertReviewControllerApi.getExpertBranches(),
		{
			select: ({ data }) => {
				const seen = new Set<number>();
				return (data.data ?? [] as BranchLessonResponse[])
					.filter((b: BranchLessonResponse) => {
						if (!b.branchId || seen.has(b.branchId)) return false;
						seen.add(b.branchId);
						return true;
					})
					.map((b: BranchLessonResponse) => ({ id: String(b.branchId), name: b.branchName ?? '' }));
			},
			staleTime: Infinity,
			refetchOnWindowFocus: false,
		}
	);

	const { data: reasonOptions = [] } = useQueryProvider(
		[QUERY_KEYS.getExpertReviewReasons],
		() => ExpertReviewControllerApi.getExpertReviewReasons(),
		{
			select: ({ data }) =>
				(data.data ?? [] as ExpertReviewReasonResponse[])
					.filter(r => r.isVisible !== false)
					.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
			staleTime: Infinity,
			refetchOnWindowFocus: false,
			enabled: modalRating > 0 && modalRating < 4,
		}
	);

	const isReassignedStatus = selectedStatus?.id === ReviewStatus.REASSIGNED_PENDING || selectedStatus?.id === ReviewStatus.REASSIGNED_REVIEWED;
	const alreadyReviewedParam = selectedStatus?.id === ReviewStatus.REVIEWED || selectedStatus?.id === ReviewStatus.REASSIGNED_REVIEWED;

	const { data: reviewsData, isLoading: isReviewsLoading } = useQueryProvider(
		[QUERY_KEYS.getReviewsByExpertId, selectedBranch?.id, alreadyReviewedParam, currentPage],
		() =>
			ExpertReviewControllerApi.getReviewsByExpertId(
				selectedBranch.id !== 'all' ? Number(selectedBranch.id) : undefined,
				alreadyReviewedParam,
				currentPage - 1,
				ITEMS_PER_PAGE,
			),
		{
			select: ({ data }) => data.data as PageExpertReviewWithQuestionDetailsResponse,
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: !isReassignedStatus,
		}
	);

	const reviewQuestionIds = (reviewsData?.content ?? []).map(q => q.questionId).filter((id): id is number => !!id);

	// OE-10888: bir sorunun uzmana yeniden atanıp atanmadığı findActiveOriginalQuestionIds ile öğreniliyor;
	// dönen id'ler review satırlarının questionId'sine (=originalQuestionId) karşılık geliyor.
	const { data: activeReassignedQuestionIds } = useQueryProvider(
		[QUERY_KEYS.findActiveOriginalQuestionIds, reviewQuestionIds.join(',')],
		() => ExpertResolveQuestionControllerApi.findActiveOriginalQuestionIds(reviewQuestionIds),
		{
			select: ({ data }) => new Set(data.data ?? []),
			refetchOnWindowFocus: false,
			enabled: !isReassignedStatus && reviewQuestionIds.length > 0,
		}
	);

	const { data: resolveQueueData, isLoading: isResolveQueueLoading } = useQueryProvider(
		[QUERY_KEYS.listResolveQuestions, selectedStatus?.id, currentPage],
		() =>
			ExpertResolveQuestionControllerApi.listResolveQuestions(
				alreadyReviewedParam ? SOLVED_RESOLVE_STATUSES : PENDING_RESOLVE_STATUSES,
				currentPage - 1,
				ITEMS_PER_PAGE,
			),
		{
			select: ({ data }) => data.data,
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isReassignedStatus,
		}
	);

	const isLoading = isReassignedStatus ? isResolveQueueLoading : isReviewsLoading;

	const questions: ReviewRow[] = isReassignedStatus
		? (resolveQueueData?.content ?? []).map(
				(r): ReviewRow => ({
					questionId: r.questionId,
					questionImage: r.questionImage,
					isResolveQueueRow: true,
					resolveQuestionStatus: r.questionStatus,
				})
			)
		: (reviewsData?.content ?? []).map(
				(q): ReviewRow => ({
					...q,
					reassignedToExpert: !!q.questionId && !!activeReassignedQuestionIds?.has(q.questionId),
				})
			);
	const totalPages: number = (isReassignedStatus ? resolveQueueData?.totalPages : reviewsData?.totalPages) ?? 0;

	const currentQuestion = questions[currentQuestionIndex] ?? null;

	const closeModal = useCallback(() => {
		setIsModalOpen(false);
		setModalRating(0);
		setHoveredStar(0);
		setSelectedReasons([]);
	}, []);

	const handleNextOrClose = useCallback(() => {
		const next = currentQuestionIndex + 1;
		if (next < questions.length) {
			setCurrentQuestionIndex(next);
			setModalRating(0);
			setSelectedReasons([]);
			setHoveredStar(0);
		} else {
			closeModal();
		}
	}, [currentQuestionIndex, questions.length, closeModal]);

	const { safeMutate: saveReview, isLoading: isSaving } = useMutationProvider(
		(vars: { questionId: number; ratingValue: number; reasonIds: number[] }) =>
			ExpertReviewControllerApi.createExpertReview({
				questionId: vars.questionId,
				ratingValue: vars.ratingValue,
				reasonIds: vars.reasonIds,
			}),
		{
			onSuccess: () => {
				queryClient.invalidateQueries([QUERY_KEYS.getReviewsByExpertId]);
				handleNextOrClose();
			},
		}
	);

	const openModal = useCallback((question: ReviewRow, index: number) => {
		// Bana Ata kuyruğu satırlarında puan/video verisi yok, bu yüzden değerlendirme modalı açılmıyor.
		if (question.isResolveQueueRow) return;
		setCurrentQuestionIndex(index);
		setModalRating(0);
		setSelectedReasons([]);
		setIsModalOpen(true);
	}, []);

	const { safeMutate: assignToMe, isLoading: isAssigning } = useMutationProvider(
		(vars: { questionId: number; curriculumName?: string; curriculumElementUnitName?: string }) =>
			ExpertResolveQuestionControllerApi.addToResolveQueue({ questionId: vars.questionId }),
		{
			onSuccess: ({ data }, vars) => {
				const resolveQuestion = data.data as ExpertResolveQuestionResponse | undefined;
				if (!resolveQuestion?.id || !resolveQuestion?.questionId) return;

				navigate(NavigationPath.solverSolve, {
					state: {
						resolveQuestion: {
							id: resolveQuestion.id,
							questionId: resolveQuestion.questionId,
							interactionId: resolveQuestion.interactionId,
							questionImage: resolveQuestion.questionImage,
							curriculumName: vars.curriculumName,
							curriculumElementUnitName: vars.curriculumElementUnitName,
						},
					},
				});
			},
		}
	);

	const handleAssignToMe = useCallback(
		(question: ReviewRow, e?: React.MouseEvent) => {
			e?.stopPropagation();
			if (!question.questionId || question.reassignedToExpert) return;
			assignToMe({
				questionId: question.questionId,
				curriculumName: question.curriculumName,
				curriculumElementUnitName: question.curriculumElementUnitName,
			});
		},
		[assignToMe]
	);

	const handleSaveAndNext = useCallback(() => {
		if (!currentQuestion?.questionId) return;
		saveReview({
			questionId: currentQuestion.questionId,
			ratingValue: modalRating,
			reasonIds: selectedReasons,
		});
	}, [currentQuestion, modalRating, selectedReasons, saveReview]);

	const isRatingIncomplete = modalRating === 0 || (modalRating < 4 && selectedReasons.length === 0);

	const handleSaveAndAssign = useCallback(() => {
		if (!currentQuestion?.questionId) return;
		saveReview({
			questionId: currentQuestion.questionId,
			ratingValue: modalRating,
			reasonIds: selectedReasons,
		});
		handleAssignToMe(currentQuestion);
	}, [currentQuestion, modalRating, selectedReasons, saveReview, handleAssignToMe]);

	const toggleReason = useCallback((id: number) => {
		setSelectedReasons(prev => (prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]));
	}, []);

	const handleImageClick = useCallback((url: string) => {
		setLightboxImage(url);
		setIsLightboxOpen(true);
	}, []);

	const handleBranchSelect = useCallback((opt: DropdownOption) => {
		setSelectedBranch(opt);
		setCurrentPage(1);
	}, []);

	const handleStatusSelect = useCallback((opt: DropdownOption | null) => {
		setSelectedStatus(opt);
		setCurrentPage(1);
	}, []);

	const renderInteractiveStars = () => {
		const display = hoveredStar || modalRating;
		return [...Array(5)].map((_, i) => (
			<i
				key={i}
				className={`icons-star interactive ${i < display ? '' : 'inactive'}`}
				onClick={() => setModalRating(i + 1)}
				onMouseEnter={() => setHoveredStar(i + 1)}
				onMouseLeave={() => setHoveredStar(0)}
			/>
		));
	};

	const renderReadonlyStars = (value: number | null | undefined) => {
		const rating = value ?? 0;
		return [...Array(5)].map((_, i) => (
			<i key={i} className={`icons-star ${i < rating ? '' : 'inactive'}`} />
		));
	};

	const renderDesktopRow = (question: ReviewRow, index: number) => (
		<div
			key={question.questionId}
			className={`er-row${question.isResolveQueueRow ? ' er-row--readonly' : ''}`}
			onClick={() => openModal(question, index)}>
			<div className="er-col--id">#{question.questionId}</div>
			<div className="er-col--lesson">{question.curriculumName ?? '-'}</div>
			<div className="er-col--unit">{question.curriculumElementUnitName ?? '-'}</div>
			<div className="er-col--image">
				{question.questionImage ? (
					<img
						src={question.questionImage}
						alt="Soru görseli"
						className="er-thumb"
						onClick={e => {
							e.stopPropagation();
							handleImageClick(question.questionImage!);
						}}
					/>
				) : (
					<span className="er-rating-zero">-</span>
				)}
			</div>
			<div className="er-col--video">
				{question.isResolveQueueRow ? (
					<span className="er-rating-zero">-</span>
				) : (
					<div className="er-video-thumb">
						<i className="icons-play-circle" />
					</div>
				)}
			</div>
			<div className="er-col--rating">
				{question.ratingValue !== null && question.ratingValue !== undefined ? (
					<span className="er-rating-value">{question.ratingValue}</span>
				) : (
					<span className="er-rating-zero">-</span>
				)}
			</div>
			<div className="er-col--action">
				{question.isResolveQueueRow || !isActiveAssignToMe ? (
					<span className="er-rating-zero">-</span>
				) : (
					<button
						className="er-assign-btn"
						disabled={isAssigning || question.reassignedToExpert}
						onClick={e => handleAssignToMe(question, e)}>
						Bana Ata
					</button>
				)}
			</div>
		</div>
	);

	const renderMobileCard = (question: ReviewRow, index: number) => (
		<div
			key={question.questionId}
			className={`er-mobile-card${question.isResolveQueueRow ? ' er-mobile-card--readonly' : ''}`}
			onClick={() => openModal(question, index)}>
			<div className="er-mobile-card__header">
				<span className="er-mobile-card__id">#{question.questionId}</span>
				<span className={`er-mobile-card__status ${alreadyReviewedParam ? '--reviewed' : '--pending'}`}>
					{isReassignedStatus ? (alreadyReviewedParam ? 'Yeniden Yanıtlandı' : 'Yeniden Yanıt Bekliyor') : alreadyReviewedParam ? 'Değerlendirildi' : 'Bekliyor'}
				</span>
			</div>
			<div className="er-mobile-card__lesson">{question.curriculumName ?? '-'}</div>
			<div className="er-mobile-card__meta">{question.curriculumElementUnitName ?? '-'}</div>
			<div className="er-mobile-card__media">
				{question.questionImage && (
					<img
						src={question.questionImage}
						alt="Soru görseli"
						className="er-mobile-card__img"
						onClick={e => {
							e.stopPropagation();
							handleImageClick(question.questionImage!);
						}}
					/>
				)}
				{!question.isResolveQueueRow && (
					<div className="er-video-thumb">
						<i className="icons-play-circle" />
					</div>
				)}
			</div>
			<div className="er-mobile-card__footer">
				<span className="er-mobile-card__rating-label">Uzman Puanı:</span>
				{question.ratingValue !== null && question.ratingValue !== undefined ? (
					<span className="er-rating-value">{question.ratingValue}</span>
				) : (
					<span className="er-rating-zero">-</span>
				)}
				{!question.isResolveQueueRow && isActiveAssignToMe && (
					<button
						className="er-assign-btn er-mobile-card__assign-btn"
						disabled={isAssigning || question.reassignedToExpert}
						onClick={e => handleAssignToMe(question, e)}>
						Bana Ata
					</button>
				)}
			</div>
		</div>
	);

	const renderEmptyState = () => {
		if (isLoading) return <div className="er-empty">Yükleniyor...</div>;
		return <div className="er-empty">Gösterilecek soru bulunamadı.</div>;
	};

	return (
		<div className="expert-review">
			<div className="er-header">
				<h1>Uzman Soru Değerlendirme Paneli</h1>
			</div>

			<div className="er-filters">
				<Dropdown
					options={[ALL_BRANCHES_OPTION, ...branchOptions]}
					selectedOption={selectedBranch}
					onOptionSelect={handleBranchSelect}
					placeholder="Branş Seçin"
					fullWidth
				/>
				<Dropdown
					options={statusOptions}
					selectedOption={selectedStatus}
					onOptionSelect={handleStatusSelect}
					placeholder="Durum Seçin"
					fullWidth
				/>
			</div>

			{isDesktop ? (
				<div className="er-table">
					<div className="er-table__header">
						<div className="er-col">Soru ID</div>
						<div className="er-col">Müfredat</div>
						<div className="er-col">Ünite</div>
						<div className="er-col er-col--image">Soru Görseli</div>
						<div className="er-col er-col--video">Video Cevabı</div>
						<div className="er-col er-col--rating">Uzman Puanı</div>
						<div className="er-col er-col--action">İşlem</div>
					</div>
					<div className="er-table__body">
						{questions.length > 0 ? questions.map((q, i) => renderDesktopRow(q, i)) : renderEmptyState()}
					</div>
				</div>
			) : (
				<div className="er-mobile-list">
					{questions.length > 0 ? questions.map((q, i) => renderMobileCard(q, i)) : renderEmptyState()}
				</div>
			)}

			{totalPages > 1 && (
				<div className="er-pagination">
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={setCurrentPage}
					/>
				</div>
			)}

			<Modal
				isOpen={isModalOpen}
				onClose={closeModal}
				closeOnEsc={!isLightboxOpen}
				closeOnClickOutside={!isLightboxOpen}
				variant={isDesktop ? 'centered' : 'bottom'}
				size="xl"
				className="er-rating-modal"
				showCloseButton
				hasHeader
				title={currentQuestion ? `Soru #${currentQuestion.questionId} Değerlendirme` : ''}>
				{currentQuestion && (
					<div className="er-modal__body">
						<div className="er-modal__media">
							<div className="er-modal__question-img">
								{currentQuestion.questionImage && (
									<img
										src={currentQuestion.questionImage}
										alt={`Soru #${currentQuestion.questionId}`}
										style={{ cursor: 'zoom-in' }}
										onClick={() => handleImageClick(currentQuestion.questionImage!)}
									/>
								)}
							</div>
							<div className="er-modal__video-section">
								<div className="er-modal__video-player">
									{currentQuestion.solutionVideo ? (
										<video
											src={currentQuestion.solutionVideo}
											controls
											className="er-modal__video"
										/>
									) : (
										<div className="er-modal__video-placeholder">
											<i className="icons-play-circle" />
											<span>Video Yükleniyor...</span>
										</div>
									)}
								</div>
								<div className="er-modal__rating-section">
									<p className="er-modal__rating-label">
										{alreadyReviewedParam ? 'Verilen Puan:' : 'Çözümü 1-5 arası puanlayın:'}
									</p>
									<div className={`er-modal__stars${alreadyReviewedParam ? ' er-modal__stars--readonly' : ''}`}>
										{alreadyReviewedParam
											? renderReadonlyStars(currentQuestion.ratingValue)
											: renderInteractiveStars()}
									</div>
								</div>
							</div>
						</div>

						{modalRating > 0 && modalRating < 4 && (
							<div className="er-modal__reasons">
								<div className="er-modal__reasons-grid">
									{reasonOptions.map(reason => {
										if (reason.id === undefined) return null;
										const checked = selectedReasons.includes(reason.id);
										return (
											<div
												key={reason.id}
												className="er-reason-item"
												onClick={() => toggleReason(reason.id)}>
												<span
													className="er-reason-item__checkmark"
													style={checked ? { background: 'var(--brand-fill-strong)', borderColor: 'var(--brand-fill-strong)' } : undefined}>
													{checked && (
														<svg
															viewBox="0 0 10 8"
															fill="none"
															xmlns="http://www.w3.org/2000/svg"
															style={{ width: 10, height: 8 }}>
															<path
																d="M1 4L3.5 6.5L9 1"
																stroke="white"
																strokeWidth="1.8"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														</svg>
													)}
												</span>
												<span className="er-reason-item__label">{reason.name}</span>
											</div>
										);
									})}
								</div>
							</div>
						)}

						<div className="er-modal__actions">
							{alreadyReviewedParam ? (
								<button
									className="er-modal__btn er-modal__btn--save"
									onClick={closeModal}>
									Kapat
								</button>
							) : (
								<>
									<button
										className="er-modal__btn er-modal__btn--cancel"
										onClick={closeModal}>
										İptal
									</button>
									{isActiveAssignToMe && (
										<button
											className="er-modal__btn er-modal__btn--assign"
											disabled={isAssigning || isSaving || currentQuestion.reassignedToExpert || isRatingIncomplete}
											onClick={handleSaveAndAssign}>
											Puanı Kaydet ve Bana Ata
										</button>
									)}
									<button
										className="er-modal__btn er-modal__btn--save"
										onClick={handleSaveAndNext}
										disabled={isSaving || isRatingIncomplete}>
										Puanı Kaydet &amp; Sonrakine Geç
									</button>
								</>
							)}
						</div>
					</div>
				)}
			</Modal>

			<Lightbox
				open={isLightboxOpen}
				close={() => setIsLightboxOpen(false)}
				carousel={{ finite: true }}
				controller={{ closeOnBackdropClick: true }}
				render={{ buttonPrev: () => null, buttonNext: () => null }}
				slides={[{ src: lightboxImage, width: 920, height: 520 }]}
				plugins={[Zoom]}
				zoom={{ maxZoomPixelRatio: 5, doubleTapDelay: 300, doubleClickDelay: 300 }}
				styles={{ root: { '--yarl__portal_zindex': '100000' } as React.CSSProperties }}
			/>
		</div>
	);
};

export default ExpertReviewScreen;
